<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import {
  ComboboxAnchor,
  ComboboxContent,
  ComboboxInput,
  ComboboxPortal,
  ComboboxRoot,
  ComboboxViewport,
} from 'reka-ui'

import GlobalSearchScopeSelector from '~/components/search/GlobalSearchScopeSelector.vue'
import SourceKbd from '~/components/ui/SourceKbd.vue'
import { useGlobalSearch } from '~/composables/search/useGlobalSearch'
import { useEffectiveGardianRoles } from '~/composables/auth/useEffectiveGardianRoles'
import { useShortcutKeyVisualFeedback } from '~/composables/ui/useShortcutKeyVisualFeedback'
import { useOverlayPopperZ } from '~/composables/ui/useOverlayStack'
import { isDeveloper } from '~~/utils/auth/abilities'
import { isApplePlatform } from '~/utils/isApplePlatform'
import {
  ASYNC_OVERLAY_DELAY_MS,
  defineAsyncWithSkeleton,
} from '~/utils/defineAsyncWithSkeleton'

const GlobalSearchResultsPanel = defineAsyncWithSkeleton(
  function loadGlobalSearchResultsPanel() {
    return import('~/components/search/GlobalSearchResultsPanel.vue')
  },
  undefined,
  ASYNC_OVERLAY_DELAY_MS,
)

const GlobalSearchFeedbackPrompt = defineAsyncWithSkeleton(
  function loadGlobalSearchFeedbackPrompt() {
    return import('~/components/search/GlobalSearchFeedbackPrompt.vue')
  },
  undefined,
  ASYNC_OVERLAY_DELAY_MS,
)

defineOptions({ name: 'GlobalSearchBar' })

const overlayPopperZ = useOverlayPopperZ()

const {
  query,
  status,
  panelOpen,
  topResults,
  groupedResults,
  availableScopeTypes,
  resultFilterTypes,
  scopeTypes,
  scopeRefetching,
  flatSelectableResults,
  hasMinimumQuery,
  showFeedbackPrompt,
  feedbackAlreadySubmitted,
  openPanel,
  closePanel,
  toggleScopeType,
  clearScopeTypes,
  clearQuery,
  selectResult,
  submitFeedback,
} = useGlobalSearch()

const { roles } = useEffectiveGardianRoles()
const showDebugScore = computed(() => isDeveloper(roles.value))

const showFeedbackFooter = computed(function resolveShowFeedbackFooter() {
  return (
    showFeedbackPrompt.value &&
    !feedbackAlreadySubmitted.value &&
    (status.value === 'ready' || status.value === 'empty')
  )
})

const hasQueryText = computed(() => query.value.trim().length > 0)

const comboboxValue = ref('')
/** Ref sur le wrapper natif autour de ComboboxInput - querySelector garantit l'élément DOM réel. */
const anchorWrapperRef = ref<HTMLDivElement | null>(null)

/** Détection plateforme après montage pour éviter un mismatch SSR (Ctrl côté serveur vs ⌘ côté client). */
const isMac = ref(false)

const searchShortcut = computed(() => ({
  key: 'k',
  meta: isMac.value,
  ctrl: !isMac.value,
  shift: false,
  alt: false,
}))

const shortcutHintLabel = computed(() =>
  isMac.value
    ? 'Mettre le focus sur la recherche (raccourci ⌘K)'
    : 'Mettre le focus sur la recherche (raccourci Ctrl+K)'
)

const {
  keys: shortcutKeys,
  pressedKeys: shortcutPressedKeys,
  sublimationTriggered: shortcutSublimation,
  onKeyDown: shortcutOnKeyDown,
  onKeyUp: shortcutOnKeyUp,
} = useShortcutKeyVisualFeedback(searchShortcut)

onMounted(function mountSearchShortcutListeners() {
  isMac.value = isApplePlatform()
  document.addEventListener('keydown', shortcutOnKeyDown)
  document.addEventListener('keyup', shortcutOnKeyUp)
})

onBeforeUnmount(function unmountSearchShortcutListeners() {
  document.removeEventListener('keydown', shortcutOnKeyDown)
  document.removeEventListener('keyup', shortcutOnKeyUp)
})

const comboboxOpen = computed({
  get() {
    return panelOpen.value && hasMinimumQuery.value
  },
  set(nextOpen: boolean) {
    if (nextOpen) {
      openPanel()
      return
    }
    closePanel()
  },
})

function focusSearchInput(): void {
  const inputEl = anchorWrapperRef.value?.querySelector<HTMLInputElement>('input.global-search__input')
  inputEl?.focus()
  if (hasMinimumQuery.value) {
    openPanel()
  }
}

function onInputFocus(): void {
  if (hasMinimumQuery.value) {
    openPanel()
  }
}

function onClearQuery(event: MouseEvent): void {
  event.preventDefault()
  event.stopPropagation()
  clearQuery()
  focusSearchInput()
}

function onListWheel(wheelEvent: WheelEvent): void {
  wheelEvent.stopPropagation()
}

function onResultsAutoFocus(event: Event): void {
  event.preventDefault()
}

async function onSelectResult(selectedValue: string): Promise<void> {
  if (!selectedValue || selectedValue.trim().length === 0) {
    return
  }

  const matchedResult = flatSelectableResults.value.find(function findSelectedResult(resultItem) {
    const resultValue = resultItem.routePath ?? resultItem.title
    return resultValue === selectedValue
  })
  if (!matchedResult) {
    return
  }

  await selectResult(matchedResult)
  comboboxValue.value = ''
}

defineExpose({
  focusSearchInput,
})
</script>

<template>
  <div ref="anchorWrapperRef" class="global-search" data-onboarding="global-search">
    <ComboboxRoot
      v-model="comboboxValue"
      v-model:open="comboboxOpen"
      :ignore-filter="true"
      @update:model-value="onSelectResult"
    >
      <ComboboxAnchor class="global-search__anchor">
        <div class="global-search__scope">
          <GlobalSearchScopeSelector
            :scope-types="scopeTypes"
            :available-types="availableScopeTypes"
            @toggle-type="toggleScopeType"
            @clear="clearScopeTypes"
          />
        </div>
        <span class="global-search__scope-separator" aria-hidden="true" />
        <span class="global-search__search-icon material-symbols-rounded" aria-hidden="true">search</span>
        <ComboboxInput
          v-model="query"
          class="global-search__input"
          placeholder="Rechercher dans Mon Orga…"
          autocomplete="off"
          role="combobox"
          aria-autocomplete="list"
          :aria-expanded="comboboxOpen"
          :aria-controls="'global-search-results'"
          aria-label="Recherche globale"
          @focus="onInputFocus"
        />
        <button
          v-if="hasQueryText"
          type="button"
          class="global-search__clear-btn"
          aria-label="Effacer la recherche"
          @mousedown.prevent="onClearQuery"
        >
          <span class="material-symbols-rounded" aria-hidden="true">close</span>
        </button>
        <button
          type="button"
          class="global-search__shortcut-btn"
          :aria-label="shortcutHintLabel"
          tabindex="-1"
          @click.stop="focusSearchInput"
        >
          <SourceKbd
            :keys="shortcutKeys"
            :pressed-keys="shortcutPressedKeys"
            :sublimation="shortcutSublimation"
          />
        </button>
      </ComboboxAnchor>

      <div id="global-search-results">
        <ComboboxPortal :disabled="true">
          <ComboboxContent
            class="global-search__content"
          position="popper"
          side="bottom"
          :align="'start'"
          :side-offset="6"
          :style="{ zIndex: overlayPopperZ }"
        >
          <ComboboxViewport
            class="global-search__viewport"
            @wheel="onListWheel"
            @focusin="onResultsAutoFocus"
          >
            <GlobalSearchResultsPanel
              :status="status"
              :top-results="topResults"
              :grouped-results="groupedResults"
              :result-filter-types="resultFilterTypes"
              :scope-types="scopeTypes"
              :scope-refetching="scopeRefetching"
              :show-debug-score="showDebugScore"
              @toggle-type="toggleScopeType"
              @clear="clearScopeTypes"
            />
          </ComboboxViewport>
          <GlobalSearchFeedbackPrompt
            v-if="showFeedbackFooter"
            @submit="submitFeedback"
          />
        </ComboboxContent>
      </ComboboxPortal>
      </div>
    </ComboboxRoot>
  </div>
</template>
